import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Spinner,
  Badge,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import NoData from "../../NoData";

const InventoryViewModal = ({ show, handleClose, stockId }) => {
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStockDetails = async () => {
      if (!stockId) return;
      setLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/api/inventory-logs/${stockId}/`,
          { headers: HEADERS },
        );
        setStockData(response.data);
      } catch (error) {
        console.error("Error fetching stock detail:", error);
      } finally {
        setLoading(false);
      }
    };
    if (show) fetchStockDetails();
  }, [show, stockId]);

  const renderText = (text) => (
    <span title={text || "—"} className="inv-text-truncate">
      {text?.length > 30 ? `${text.slice(0, 30)}...` : text || "—"}
    </span>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="inventory-modal"
    >
      <Modal.Header closeButton className="inventory-modal-header">
        <Modal.Title className="inventory-modal-title">
          <span className="me-5">{stockData?.item || "Inventory Details"}</span>
          <Badge bg="info" className="inventory-badge ">
            Qty: {stockData?.quantity ?? 0}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="inventory-modal-body">
        {loading ? (
          <div className="inventory-loading">
            <Spinner animation="border" variant="primary" />
            <p>Loading inventory data...</p>
          </div>
        ) : !stockData ? (
          <NoData message="No inventory data found." height="300px" />
        ) : (
          <Container fluid>
            <div id="inventory-basic-section" className="inventory-section">
              <Row>
                <Col md={6} xs={12}>
                  <p>
                    <strong>Item:</strong>{" "}
                    {renderText(stockData?.item || "N/A")}
                  </p>
                  <p>
                    <strong>Description:</strong>{" "}
                    {renderText(stockData?.description || "N/A")}
                  </p>
                </Col>
                <Col md={6} xs={12}>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {moment(stockData?.created_at).format("DD-MM-YYYY")}
                  </p>
                  <p>
                    <strong>Created By:</strong>{" "}
                    {renderText(stockData?.created_by || "N/A")}
                  </p>
                </Col>
              </Row>
            </div>
            <div id="inventory-added-section" className="inventory-section">
              <h6 className="inventory-section-title">Added Logs</h6>
              <div className="inventory-table-wrapper">
                {stockData?.inventory_item?.length > 0 ? (
                  <table className="inventory-table">
                    <thead>
                      <tr>
                        <th>Added Qty</th>
                        <th>Updated By</th>
                        <th>Updated On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockData.inventory_item.map((log, idx) => (
                        <tr key={idx}>
                          <td>{log.add_quantity || 0}</td>
                          <td>{renderText(log.updated_by)}</td>
                          <td>
                            {moment(log.updated_on).format(
                              "DD-MM-YYYY hh:mm A",
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <NoData message="No added logs found." height="100px" />
                )}
              </div>
            </div>
            <div id="inventory-issued-section" className="inventory-section">
              <h6 className="inventory-section-title">Issued Logs</h6>
              <div className="inventory-table-wrapper">
                {stockData?.issued?.length > 0 ? (
                  <table className="inventory-table">
                    <thead>
                      <tr>
                        <th>Req ID</th>
                        <th>Qty</th>
                        <th>Issued On</th>
                        <th>Issued By</th>
                        <th>Customer</th>
                        <th>Sub Customer</th>
                        <th>Address</th>
                        <th>Phone No.</th>
                        <th>Region</th>
                        <th>Assigned To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockData.issued.map((issue, idx) => (
                        <tr key={idx}>
                          <td>{issue.material_req_id || "—"}</td>
                          <td>{issue.quantity || 0}</td>
                          <td>
                            {moment(issue.issued_on).format(
                              "DD-MM-YYYY hh:mm A",
                            )}
                          </td>
                          <td>
                            {issue.approved_by
                              ? `${issue.approved_by.first_name || ""} ${
                                  issue.approved_by.last_name || ""
                                }`.trim()
                              : "N/A"}
                          </td>
                          <td>
                            {renderText(
                              issue?.ticket?.customerDetails?.customer_name ||
                                issue?.customerDetails?.customer_name,
                            )}
                          </td>
                          <td>
                            {renderText(
                              issue?.ticket?.subCustomerDetails
                                ?.customer_name ||
                                issue?.subCustomerDetails?.customer_name,
                            )}
                          </td>
                          <td>
                            {renderText(
                              issue?.ticket?.ticket_address ||
                                issue?.subCustomerDetails?.address ||
                                issue?.customerDetails?.address,
                            )}
                          </td>
                          <td>
                            {renderText(
                              issue?.ticket?.phone_number ||
                                issue?.subCustomerDetails?.phone_number ||
                                issue?.customerDetails?.phone_number,
                            )}
                          </td>
                          <td>
                            {renderText(
                              issue?.ticket?.region ||
                                issue?.subCustomerDetails?.region ||
                                issue?.customerDetails?.region,
                            )}
                          </td>
                          <td>
                            {issue?.ticket?.assignTo
                              ? `${issue.ticket.assignTo.first_name || ""} ${
                                  issue.ticket.assignTo.last_name || ""
                                }`.trim()
                              : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <NoData message="No issued logs found." height="100px" />
                )}
              </div>
            </div>
          </Container>
        )}
      </Modal.Body>

      <Modal.Footer className="inventory-modal-footer">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InventoryViewModal;
